import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../styling/stylingHelper';
import type { ClientTrackerResponse } from '../../../../services/admin/clientAnalyticsService';
import {
  sumMonths,
  sumRestaurants,
  costPctRatio,
  formatPct,
  formatPp,
  trendColor,
  type Lens,
  type MatrixColumn,
} from './trackerUtils';
import { RestaurantTrendChart } from './RestaurantTrendChart';

interface CostMatrixViewProps {
  data: ClientTrackerResponse;
  columns: MatrixColumn[];
  lens: Lens;
}

const quarterCol = 'bg-gray-50 dark:bg-gray-800/40';

export function CostMatrixView({ data, columns, lens }: CostMatrixViewProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const allRestaurants = data.clients.flatMap((c) => c.restaurants);
  const colCount = 1 + columns.length + 6;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <th className={cn('text-left font-medium px-3 py-2 sticky left-0 bg-white dark:bg-gray-900', secondaryText)}>
            Client / Restaurant
          </th>
          {columns.map((col) => (
            <th
              key={col.key}
              className={cn('text-right font-medium px-3 py-2 whitespace-nowrap', secondaryText, col.type === 'quarter' && quarterCol)}
            >
              {col.label}
            </th>
          ))}
          <th className={cn('text-left font-medium px-3 py-2', secondaryText)}>CC start</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Before</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>After</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Δ</th>
          <th className={cn('text-left font-medium px-3 py-2', secondaryText)}>Trend</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Months</th>
        </tr>
      </thead>
      <tbody>
        {data.clients.map((client) => {
          const clientMonthsFilled = client.restaurants.reduce((sum, r) => sum + r.monthsFilled, 0);
          return (
            <Fragment key={client.clientId}>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <td className={cn('px-3 py-2 sticky left-0 bg-gray-50 dark:bg-gray-800/50', primaryText, semiBoldText)}>
                  {client.clientName}
                </td>
                {columns.map((col) => {
                  const ratio = costPctRatio(sumRestaurants(client.restaurants, col.months, lens));
                  return (
                    <td key={col.key} className={cn('px-3 py-2 text-right', semiBoldText, primaryText, col.type === 'quarter' && quarterCol)}>
                      {formatPct(ratio)}
                    </td>
                  );
                })}
                <td className={cn('px-3 py-2', smallTextSize, secondaryText)}>{client.ccStartDate ?? '—'}</td>
                <td className="px-3 py-2" colSpan={4} />
                <td className={cn('px-3 py-2 text-right', smallTextSize, secondaryText)}>{clientMonthsFilled}</td>
              </tr>

              {client.restaurants.map((r) => {
                const ba = r.beforeAfter;
                const isExpanded = expanded.has(r.restaurantId);
                return (
                  <Fragment key={r.restaurantId}>
                    <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                      <td className="px-3 py-2 sticky left-0 bg-white dark:bg-gray-900">
                        <button
                          onClick={() => toggle(r.restaurantId)}
                          className="flex items-center gap-1.5 text-left text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400"
                        >
                          {isExpanded ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />}
                          <span className={cn(smallTextSize, 'pl-1')}>{r.restaurantName}</span>
                        </button>
                      </td>
                      {columns.map((col) => {
                        const ratio = costPctRatio(sumMonths(r.months, col.months, lens));
                        return (
                          <td key={col.key} className={cn('px-3 py-2 text-right', smallTextSize, primaryText, col.type === 'quarter' && quarterCol)}>
                            {formatPct(ratio)}
                          </td>
                        );
                      })}
                      <td className={cn('px-3 py-2', smallTextSize, secondaryText)}>{ba?.ccStartDate ?? '—'}</td>
                      <td className={cn('px-3 py-2 text-right', smallTextSize, secondaryText)}>{formatPct(ba?.costPctBefore ?? null)}</td>
                      <td className={cn('px-3 py-2 text-right', smallTextSize, secondaryText)}>{formatPct(ba?.costPctAfter ?? null)}</td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right',
                          smallTextSize,
                          semiBoldText,
                          ba?.deltaPp != null && ba.deltaPp > 0
                            ? 'text-green-600 dark:text-green-400'
                            : ba?.deltaPp != null && ba.deltaPp < 0
                              ? 'text-red-600 dark:text-red-400'
                              : secondaryText
                        )}
                      >
                        {formatPp(ba?.deltaPp ?? null)}
                      </td>
                      <td className={cn('px-3 py-2', smallTextSize, trendColor(ba?.trend ?? null))}>{ba?.trend ?? '—'}</td>
                      <td className={cn('px-3 py-2 text-right', smallTextSize, secondaryText)}>{r.monthsFilled}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
                        <td colSpan={colCount} className="px-3">
                          <RestaurantTrendChart restaurant={r} lens={lens} ccStartDate={client.ccStartDate} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </Fragment>
          );
        })}

        <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800">
          <td className={cn('px-3 py-2 sticky left-0 bg-gray-100 dark:bg-gray-800', primaryText, semiBoldText)}>
            TOTAL — all clients
          </td>
          {columns.map((col) => {
            const ratio = costPctRatio(sumRestaurants(allRestaurants, col.months, lens));
            return (
              <td key={col.key} className={cn('px-3 py-2 text-right', semiBoldText, primaryText, col.type === 'quarter' && quarterCol)}>
                {formatPct(ratio)}
              </td>
            );
          })}
          <td className="px-3 py-2" colSpan={6} />
        </tr>
      </tbody>
    </table>
  );
}
